import React, { forwardRef, useId } from "react";
import { cn } from "./cn";

/* u1 Input. Two rules from the rulebook drive this component: 1. `className` merges with the defaults, never replaces them. The existing common/Input.jsx... */
const LABEL_VARIANTS = {
  default: "text-[13px] font-semibold text-[var(--ink)] tracking-[0.005em] font-sans",
  caption:
    "text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--muted)] font-sans",
};
const SIZES = {
  md: "h-11 px-3.5 text-sm rounded-[var(--radius-field)]",
  lg: "h-12 md:h-[52px] px-4 text-[15px] rounded-[10px] tracking-[-0.005em]",
};

export const Input = forwardRef(function Input(
  {
    label,
    labelVariant = "default",
    labelRight,
    hint,
    error,
    id,
    size = "md",
    className = "",
    wrapperClassName = "",
    inputWrapperClassName = "",
    trailing,
    ...rest
  },
  ref
) {
  const generatedId = useId();
  const inputId = id || generatedId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const hasError = Boolean(error);
  const showHint = Boolean(hint) && !hasError;

  return (
    <div className={cn("flex flex-col gap-2", wrapperClassName)}>
      {(label || labelRight) && (
        <div className="flex items-baseline justify-between gap-3">
          {label && (
            <label
              htmlFor={inputId}
              className={LABEL_VARIANTS[labelVariant] || LABEL_VARIANTS.default}
            >
              {label}
              {/* Decorative: the control carries `required`, so a bare
                  asterisk read aloud would only add noise. */}
              {rest.required && labelVariant === "caption" && (
                <span aria-hidden="true" className="ml-1 text-[var(--coral)]">
                  *
                </span>
              )}
            </label>
          )}
          {labelRight}
        </div>
      )}

      {/* `relative` so a trailing control (a password eye) can sit inside. */}
      <div className={cn("relative", inputWrapperClassName)}>
        <input
          ref={ref}
          id={inputId}
          aria-invalid={hasError || undefined}
          aria-describedby={cn(hasError && errorId, showHint && hintId) || undefined}
          className={cn(
            "w-full bg-white font-sans border-[1.5px]",
            "transition-[border-color,box-shadow] duration-150",
            "text-[var(--ink)] placeholder:text-[var(--muted-2)]",
            "focus:outline-none",
            SIZES[size] || SIZES.md,
            hasError
              ? "border-[#D61E1E] focus:border-[#D61E1E] focus:shadow-[0_0_0_4px_var(--focus-ring-error)]"
              : "border-[var(--field-border)] hover:border-[var(--field-border-hover)] focus:border-[var(--orange)] focus:shadow-[0_0_0_4px_var(--focus-ring)]",
            trailing && "pr-13",
            className
          )}
          {...rest}
        />
        {trailing}
      </div>

      {showHint && (
        <p id={hintId} className="text-[12px] leading-[1.45] text-[var(--muted)] font-sans">
          {hint}
        </p>
      )}
      {hasError && (
        <p id={errorId} role="alert" className="text-[12px] text-[#D61E1E] font-sans">
          {error}
        </p>
      )}
    </div>
  );
});

export default Input;
